import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  MessageSchema,
  MessageCreateInputSchema,
  MessageUpdateInputSchema,
  type Message,
  type MessageCreateInput,
  type MessageUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `message` (communication individuelle, org-wide en v0).
 *
 * Même patron que `asset` : l'entrée est VALIDÉE par Zod
 * (`MessageCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 * Les liens vers d'autres familles (fromRef, toRefs, threadRef, contact,
 * booking, invoice, ticket, campaign, review) sont de simples ids.
 *
 * Accès (cf. data-model) : la fiche veut une visibilité héritée du `threadRef`
 * (à défaut bornée aux rôles de l'app émettrice). En v0 la famille reste
 * org-wide comme le reste du socle ; ce cloisonnement fin viendra après.
 */

export type { MessageCreateInput };

/** Nom de la collection Firestore de la famille message. */
export const MESSAGES_COLLECTION = 'messages';

/**
 * Repository typé pour la famille `message`.
 * Toute lecture est validée par `MessageSchema`, toute écriture par
 * `MessageCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface MessageRepository {
  create(input: MessageCreateInput): Promise<Message>;
  update(id: string, patch: MessageUpdateInput): Promise<Message>;
  list(options?: ListOptions): Promise<Message[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Message | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Message>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Message>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Message>;
}

// Relecture : le converter generique convertit deja TOUS les Timestamps
// (standard et metier) en Date, cf. standard-fields.ts.
const messageConverter = createConverter<Message>((raw: DocumentData) => MessageSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createMessageRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): MessageRepository {
  const col = collection(db, MESSAGES_COLLECTION).withConverter(messageConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Message>(db, col, MESSAGES_COLLECTION, getActorId);

  return {
    async create(input: MessageCreateInput): Promise<Message> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = MessageCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.channel !== undefined) optional['channel'] = fields.channel;
      if (fields.direction !== undefined) optional['direction'] = fields.direction;
      if (fields.fromRef !== undefined) optional['fromRef'] = fields.fromRef;
      if (fields.toRefs !== undefined) optional['toRefs'] = fields.toRefs;
      if (fields.subject !== undefined) optional['subject'] = fields.subject;
      if (fields.body !== undefined) optional['body'] = fields.body;
      if (fields.sentAt !== undefined) optional['sentAt'] = fields.sentAt;
      if (fields.deliveryStatus !== undefined) optional['deliveryStatus'] = fields.deliveryStatus;
      if (fields.threadRef !== undefined) optional['threadRef'] = fields.threadRef;
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.bookingId !== undefined) optional['bookingId'] = fields.bookingId;
      if (fields.invoiceId !== undefined) optional['invoiceId'] = fields.invoiceId;
      if (fields.ticketId !== undefined) optional['ticketId'] = fields.ticketId;
      if (fields.campaignId !== undefined) optional['campaignId'] = fields.campaignId;
      if (fields.reviewId !== undefined) optional['reviewId'] = fields.reviewId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Message);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit (sentAt est déjà une Date issue
      // de l'entrée validée).
      return MessageSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: MessageUpdateInput): Promise<Message> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = MessageUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, MESSAGES_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec MessageSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`message ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Message[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Message | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
